'use client';

import React from 'react';
import Link from 'next/link';
import { IconArrowRight } from '@tabler/icons-react';
import { cn } from '@/lib/utils';

export interface PillButtonProps {
  children?: React.ReactNode;
  text?: string;
  href?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export const PillButton: React.FC<PillButtonProps> = ({
  children,
  text,
  href,
  onClick,
  type = 'button',
  className,
  icon,
  disabled = false,
}) => {
  const content = text || children;
  const defaultIcon = <IconArrowRight className="w-5 h-5 stroke-[2.5] transition-transform duration-300 group-hover:translate-x-0.5" />;
  const renderedIcon = icon || defaultIcon;

  const baseClasses = cn(
    'inline-flex items-center gap-4 pl-6 sm:pl-7 pr-2 py-2 rounded-full',
    'bg-white dark:bg-neutral-900',
    'border border-neutral-200/90 dark:border-neutral-700',
    'shadow-md shadow-neutral-200/60 dark:shadow-none',
    'hover:shadow-xl hover:shadow-[#c02029]/15',
    'transition-all duration-300 group hover:border-[#c02029]/40',
    disabled && 'opacity-60 cursor-not-allowed pointer-events-none',
    className
  );

  const innerContent = (
    <>
      <span className="text-base font-semibold text-[#2e1452] dark:text-white group-hover:text-[#c02029] transition-colors">
        {content}
      </span>
      <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#c02029] text-white flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:bg-[#a61b23] shadow-md shadow-[#c02029]/25 flex-shrink-0">
        {renderedIcon}
      </span>
    </>
  );

  if (href) {
    return (
      <Link href={href as never} className={baseClasses} onClick={onClick}>
        {innerContent}
      </Link>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={baseClasses}>
      {innerContent}
    </button>
  );
};

export default PillButton;
